import { Box, Flex, Text, useDisclosure, useToast } from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  archiveApi,
  ArchivedEntity,
  ArchiveSearchQuery,
} from '@texas/api/endpoints/archiveApi';
import { Role } from '@texas/api/endpoints/userApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { SearchFilterInput } from '@texas/components/SearchFilterInput';
import { AngularNavLink } from '@texas/components/shared/AngularNavLink';
import { BodyOverride } from '@texas/components/shared/BodyOverride';
import { DataTableContainer } from '@texas/components/shared/dataTable/DataTableContainer';
import { VerifyDialogWithFunction } from '@texas/components/shared/dialog/VerifyDialogWithFunction';
import { EntityDirectoryPage } from '@texas/components/shared/entityDirectory/EntityDirectoryPage';
import { useActiveSession } from '@texas/hooks/useSession';
import {
  configurationNavigationStyles,
  configurationPageStyles,
} from '@texas/resources/theme/styles/configuration';
import { hasRole } from '@texas/utils/helpers/roleHelpers';
import { ArchiveTab, archiveTabs, archiveTabRoutePath } from './archiveTabs';
import { archiveTableColumns } from './Columns';
import { getArchiveRestoreArguments } from './getArchiveRestoreArguments';

const pageSize = 50;

export function ArchivePage({ resource }: { resource?: string }) {
  const { t } = useTranslation();
  const activeTab =
    archiveTabs.find((tab) => archiveTabRoutePath(tab) === resource) ??
    archiveTabs[0];

  return (
    <BodyOverride>
      <EntityDirectoryPage
        description={t('archivePage.description')}
        title={t('navigation.archive')}
      >
        <Flex align="stretch" direction={{ base: 'column', lg: 'row' }} gap={5}>
          <Box
            as="nav"
            aria-label={t('archivePage.navigationLabel')}
            sx={configurationNavigationStyles.root}
            w={{ base: 'full', lg: '240px' }}
            flexShrink={0}
          >
            <Flex
              sx={configurationNavigationStyles.items}
              maxH={{ lg: 'calc(100dvh - 190px)' }}
            >
              {archiveTabs.map((tab) => (
                <AngularNavLink
                  key={tab.resource}
                  to="archive.entity"
                  navobj={{ resource: archiveTabRoutePath(tab) }}
                  variant={
                    tab.resource === activeTab.resource
                      ? 'configurationNavigationActive'
                      : 'configurationNavigation'
                  }
                  aria-current={
                    tab.resource === activeTab.resource ? 'page' : undefined
                  }
                >
                  <Text noOfLines={1}>{t(tab.labelKey as 'general.name')}</Text>
                </AngularNavLink>
              ))}
            </Flex>
          </Box>
          <Box flex={1} minW={0}>
            <ArchivedEntities key={activeTab.resource} tab={activeTab} />
          </Box>
        </Flex>
      </EntityDirectoryPage>
    </BodyOverride>
  );
}

function ArchivedEntities({ tab }: { tab: ArchiveTab }) {
  const { t } = useTranslation();
  const toast = useToast();
  const { currentUser } = useActiveSession();
  const canRestore = hasRole(currentUser.role, [Role.SystemAdministrator]);
  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(1);
  const [sorting, setSorting] = useState({ key: 'archived', desc: true });
  const [selected, setSelected] = useState<ArchivedEntity | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { data, refetch, loading, error } = useApiResource(
    archiveApi.getArchived,
  );
  const { request: restore, error: restoreError } = useApiRequest(
    archiveApi.restore,
  );

  useEffect(() => {
    refetch(currentUser.organizationId, tab.archivePath, {
      searchTerm: filter,
      sortBy: sorting.key,
      sortDesc: sorting.desc,
      page,
      pageSize,
    });
  }, [
    currentUser.organizationId,
    filter,
    page,
    refetch,
    sorting.desc,
    sorting.key,
    tab,
  ]);

  const query: ArchiveSearchQuery = {
    searchTerm: filter,
    sortBy: sorting.key,
    sortDesc: sorting.desc,
    page,
    pageSize,
  };
  const items = data?.items ?? [];
  const totalItems = data?.totalItems ?? 0;

  const columns = useMemo(
    () =>
      archiveTableColumns(t, tab, !!tab.hasParent, canRestore, (entity) => {
        setSelected(entity);
        onOpen();
      }),
    [canRestore, onOpen, t, tab],
  );

  const restoreSelected = async () => {
    if (!selected) return;
    const { path, options } = getArchiveRestoreArguments(tab, selected);
    const response = await restore(path, options);
    if (!response.hasError) {
      toast({
        title: t('general.entityRestored', { name: selected.name }),
        status: 'success',
        isClosable: true,
      });
      if (items.length === 1 && page > 1) {
        setPage(page - 1);
      } else {
        await refetch(currentUser.organizationId, tab.archivePath, query);
      }
    }
  };

  return (
    <Flex direction="column" gap={4}>
      <Flex sx={configurationPageStyles.toolbar}>
        <Text fontSize="lg" fontWeight="semibold" flex={1}>
          {t(tab.labelKey as 'general.name')}
        </Text>
        <SearchFilterInput
          value={filter}
          style="entityDirectory"
          placeholder={t('archivePage.searchPlaceholder')}
          onChange={(value) => {
            setFilter(value);
            setPage(1);
          }}
        />
      </Flex>
      <DataTableContainer
        error={error ?? restoreError}
        datatable={{
          columns,
          data: items,
          isLoading: loading,
          sorted: {
            ...sorting,
            onSortedChange: (nextSorting) => {
              setSorting(nextSorting);
              setPage(1);
            },
          },
          variant: 'entity-directory',
        }}
        showColumnToggle={false}
        footer={
          <Text color="entityDirectory.mutedText" fontSize="sm">
            {t('archivePage.showingResults', {
              first: totalItems ? (page - 1) * pageSize + 1 : 0,
              last: Math.min(page * pageSize, totalItems),
              total: totalItems,
            })}
          </Text>
        }
        pagination={{
          pageSize,
          totalItems,
          currentPage: page,
          onPageChange: setPage,
          variant: 'entityDirectoryPagination',
        }}
      />
      {selected && (
        <VerifyDialogWithFunction
          headerTitle={t('archivePage.restoreTitle')}
          secondaryButtonTitle={t('general.cancel')}
          primaryButtonTitle={t('general.restore')}
          isOpen={isOpen}
          onClose={() => {
            onClose();
            setSelected(null);
          }}
          toPerform={restoreSelected}
        >
          {t('archivePage.restoreConfirm', { name: selected.name })}
        </VerifyDialogWithFunction>
      )}
    </Flex>
  );
}
